import { useEffect, useMemo, useState } from "react";
import { Activity, Loader2 } from "lucide-react";
import { useAuth } from "../../context/AuthContext";

const eur = (v) => `€${Math.abs(Number(v || 0)).toFixed(2)}`;
const KINDS = {
  ticket: { label: "Tickets", cls: "text-[#1e3a5f]" }, stall: { label: "Stall", cls: "text-purple-600" },
  desk: { label: "Desk", cls: "text-sky-600" }, membership: { label: "Membership", cls: "text-emerald-700" },
  topup: { label: "Top-up", cls: "text-green-600" }, refund: { label: "Refund", cls: "text-blue-600" },
  credit: { label: "Event credit", cls: "text-amber-600" }, payout: { label: "Payout", cls: "text-slate-600" }, other: { label: "Other", cls: "text-slate-500" },
};
const SPEND = ["ticket", "stall", "desk", "membership"];

function Capsules({ options, value, onChange, testid }) {
  return (
    <div className="flex gap-1 flex-wrap" data-testid={testid}>
      {options.map(([v, label]) => (
        <button key={v} onClick={() => onChange(v)} data-testid={`${testid}-${v}`}
          className={`px-2.5 py-0.5 rounded-full text-[11px] font-medium transition-colors ${value === v ? "bg-[#1e3a5f] text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>{label}</button>
      ))}
    </div>
  );
}

/** Uniform spending activity for a member, guest or unregistered buyer (tickets, stall, desk, membership, top-ups, refunds). */
export function PersonActivityCard({ kind, id, email, refreshKey = 0 }) {
  const { authAxios } = useAuth();
  const [entries, setEntries] = useState(null);
  const [year, setYear] = useState("all");
  const [event, setEvent] = useState("all");
  const [type, setType] = useState("all");

  useEffect(() => {
    setEntries(null);
    authAxios.get("/finance-admin/people/activity", { params: { kind, id, email } })
      .then(r => setEntries(r.data.entries)).catch(() => setEntries([]));
  }, [kind, id, email, refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const years = useMemo(() => [...new Set((entries || []).map(e => e.date.slice(0, 4)).filter(Boolean))].sort().reverse(), [entries]);
  const events = useMemo(() => {
    const m = new Map();
    (entries || []).forEach(e => e.event_id && m.set(e.event_id, e.event || "Event"));
    return [...m.entries()];
  }, [entries]);
  const kinds = useMemo(() => [...new Set((entries || []).map(e => e.kind))], [entries]);
  const rows = (entries || []).filter(e => (year === "all" || e.date.startsWith(year)) && (event === "all" || e.event_id === event) && (type === "all" || e.kind === type));
  const spent = rows.filter(e => SPEND.includes(e.kind)).reduce((a, e) => a - e.amount, 0);
  const topups = rows.filter(e => e.kind === "topup").reduce((a, e) => a + e.amount, 0);
  const refunded = rows.filter(e => e.kind === "refund").reduce((a, e) => a + e.amount, 0);
  const credits = rows.filter(e => ["credit", "payout"].includes(e.kind)).reduce((a, e) => a + e.amount, 0);
  const tone = (v) => (Math.abs(v) < 0.005 ? "text-slate-500" : v > 0 ? "text-green-600" : "text-red-500");
  const sign = (v) => (Math.abs(v) < 0.005 ? "" : v > 0 ? "+" : "−");

  return (
    <div className="bg-white rounded-2xl p-4 shadow-sm mb-4" data-testid="person-activity">
      <h3 className="font-bold text-sm text-[#1e3a5f] flex items-center gap-2 mb-3"><Activity className="w-4 h-4" /> Spending activity</h3>
      {entries === null ? <div className="py-6 text-center text-slate-400"><Loader2 className="w-5 h-5 animate-spin inline" /></div>
        : entries.length === 0 ? <p className="py-6 text-center text-sm text-slate-400" data-testid="person-activity-empty">No spending activity yet</p>
        : (
          <>
            <div className="space-y-1.5 mb-3">
              <Capsules options={[["all", "All years"], ...years.map(y => [y, y])]} value={year} onChange={setYear} testid="activity-year" />
              {events.length > 0 && <Capsules options={[["all", "All events"], ...events]} value={event} onChange={setEvent} testid="activity-event" />}
              <Capsules options={[["all", "All types"], ...kinds.map(k => [k, KINDS[k]?.label || k])]} value={type} onChange={setType} testid="activity-type" />
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3 text-center">
              <div className="bg-red-50 rounded-xl p-2"><p className="font-bold text-red-500 text-sm" data-testid="activity-spent">{eur(spent)}</p><p className="text-[10px] text-slate-500">Spent</p></div>
              <div className="bg-green-50 rounded-xl p-2"><p className="font-bold text-green-600 text-sm" data-testid="activity-topups">{eur(topups)}</p><p className="text-[10px] text-slate-500">Top-ups</p></div>
              <div className="bg-blue-50 rounded-xl p-2"><p className={`font-bold text-sm ${tone(refunded)}`} data-testid="activity-returned">{sign(refunded)}{eur(refunded)}</p><p className="text-[10px] text-slate-500">Refunds</p></div>
              <div className="bg-amber-50 rounded-xl p-2"><p className={`font-bold text-sm ${tone(credits)}`} data-testid="activity-credits">{sign(credits)}{eur(credits)}</p><p className="text-[10px] text-slate-500">Credits & payouts</p></div>
            </div>
            <div className="divide-y max-h-80 overflow-y-auto" data-testid="activity-list">
              {rows.length === 0 && <p className="py-4 text-center text-xs text-slate-400">Nothing matches these filters.</p>}
              {rows.map(e => (
                <div key={`${e.kind}-${e.ref}-${e.date}`} className="py-2 flex items-start gap-2 text-xs" data-testid={`activity-row-${e.ref}`}>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-slate-700 break-words">
                      <span className={`font-bold mr-1 ${KINDS[e.kind]?.cls || ""}`}>{KINDS[e.kind]?.label || e.kind}</span>{e.label}
                      {e.status && <span className="ml-1 px-1.5 rounded-full bg-slate-100 text-slate-500 text-[10px]">{e.status}</span>}
                    </p>
                    <p className="text-[11px] text-slate-400">{[e.date ? new Date(e.date).toLocaleDateString("de-DE") : "", e.event, e.method].filter(Boolean).join(" · ")}</p>
                  </div>
                  <div className="text-right whitespace-nowrap">
                    <p className={`font-bold tabular-nums ${tone(e.amount)}`}>{sign(e.amount)}{eur(e.amount)}</p>
                    {e.paid > Math.abs(e.amount) + 0.004 && <p className="text-[10px] text-slate-400 line-through">{eur(e.paid)}</p>}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
    </div>
  );
}
